import {useState, type ReactElement} from 'react'
import {useParams, Redirect} from 'react-router-dom'
import {Trans, withNamespaces, type I18nContextValues} from 'react-i18next'
import {flowRight as compose} from 'lodash'
import moment from 'moment'
import Tooltip from '@material-ui/core/Tooltip'
import Box from '@material-ui/core/Box'
import Container from '@material-ui/core/Container'
import Card from '@material-ui/core/Card'
import Typography from '@material-ui/core/Typography'
import Grid from '@material-ui/core/Grid'
import Divider from '@material-ui/core/Divider'
import FileCopyIcon from '@material-ui/icons/FileCopy'
import Button from '@material-ui/core/Button'
import ClickAwayListener from '@material-ui/core/ClickAwayListener'
import withStyles, {StyledComponentProps} from '@material-ui/core/styles/withStyles'

import messages from '../../assets/messages'
import ExpirationTimer from './ExpirationTimer'
import CheckoutPolling from './CheckoutPolling'
import {useQueryApiV2} from '../../shared/hooks'
import {styles} from './styles'

type CryptoCheckout = {
  network: 'Bitcoin' | 'Ethereum' | 'Tron'
  digitalAsset: 'BTC' | 'ETH' | 'TRX' | 'USDT/ERC20' | 'USDT/TRC20' | 'USDC/ERC20'
  amount: number
  exchangeRate: number
  walletAddress: string
  expiresAt: number
}

function DfnsCheckoutPage({
  classes = {},
  t,
}: StyledComponentProps & {t: I18nContextValues['t']}): ReactElement | null {
  const [tooltipOpenState, setTooltipOpenState] = useState(false)
  const [isSpinnerShown, setIsSpinnerShown] = useState(false)
  const params = useParams<{id: string}>()
  const [data, {loading}] = useQueryApiV2<{accountId: number; checkoutData: CryptoCheckout}>({
    path: `/dashboard/dfns/checkout/${params.id}`,
  })

  if (loading) return null
  if (!data) return <Redirect to="/" />
  const checkoutMaxAge = data.checkoutData.expiresAt - moment().unix()
  if (checkoutMaxAge <= 0) return <Redirect to="/" />

  const {checkoutData} = data
  return (
    <Container className={classes.container}>
      <Typography variant="h1">
        <Trans {...messages.cryptoDeposit} />
      </Typography>
      <Card className={classes.card}>
        <Grid container spacing={2} className={classes.gridContainer}>
          <Grid item xs={6}>
            <Typography className={classes.rowLabel}>
              <Trans {...messages.network} />:
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography className={classes.rowValue}>{checkoutData.network}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography className={classes.rowLabel}>
              <Trans {...messages.asset} />:
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography className={classes.rowValue}>{checkoutData.digitalAsset}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography className={classes.rowLabel}>
              <Trans {...messages.amount} />:
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography className={classes.rowValue}>{checkoutData.amount}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography className={classes.rowLabel}>
              <Trans {...messages.exchangeRate} />:
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography className={classes.rowValue}>{checkoutData.exchangeRate}</Typography>
          </Grid>
          <Divider className={classes.divider} />
          <Grid container spacing={1} className={classes.gridContainer}>
            <Grid item xs={12}>
              <Typography style={{textAlign: 'center'}} className={classes.rowLabel}>
                <Trans {...messages.walletAddress} />:
              </Typography>
            </Grid>
            <Grid
              item
              xs={12}
              className={classes.pointer}
              onClick={() => navigator.clipboard.writeText(checkoutData.walletAddress)}
            >
              <ClickAwayListener
                onClickAway={() => {
                  setTooltipOpenState(false)
                }}
              >
                <Tooltip
                  PopperProps={{
                    disablePortal: true,
                  }}
                  disableHoverListener
                  disableFocusListener
                  disableTouchListener
                  open={tooltipOpenState}
                  title={t(messages.copied.i18nKey, messages.copied.defaults)}
                  placement="top-end"
                >
                  <Box
                    className={classes.walletAddressContainer}
                    onClick={() => setTooltipOpenState(true)}
                  >
                    <Typography className={classes.walletAddress}>
                      {checkoutData.walletAddress}
                    </Typography>
                    <FileCopyIcon className={classes.copyIcon} />
                  </Box>
                </Tooltip>
              </ClickAwayListener>
            </Grid>
          </Grid>
          <Grid item container xs={12} className={classes.confirmButtonRow}>
            <Button
              variant="contained"
              size="large"
              className={classes.confirmButton}
              disabled={isSpinnerShown}
              onClick={() => setIsSpinnerShown(true)}
            >
              {isSpinnerShown ? (
                <CheckoutPolling
                  checkoutId={params.id}
                  accountId={data.accountId}
                  network={checkoutData.network}
                />
              ) : (
                <Trans {...messages.confirmPaymentMessage} />
              )}
            </Button>
            <ExpirationTimer maxAge={checkoutMaxAge} />
          </Grid>
        </Grid>
      </Card>
    </Container>
  )
}

export default compose(withNamespaces(), withStyles(styles, {withTheme: true}))(DfnsCheckoutPage)
